import React from 'react';

import {observable, action} from 'mobx';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDownAZ, faArrowDownWideShort, faArrowDownZA, faStar} from '@fortawesome/free-solid-svg-icons';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import {Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Dropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';
import classNames from 'classnames';

import SearchForm from '../../../../components/common/searchForm/SearchForm';
import UpgradeButton from '../../../common/upgradeButton/UpgradeButton';

import inject from '../../../hoc/injectHoc';
import {generateSheetHeaderEntities} from './utils';
import './sheetModal.scss';

const TAB_MY_SAVED_SHEETS = 'mySavedSheets';
const TAB_SHEET_TEMPLATES = 'sheetTemplates';

const SHEET_SORT_OPTIONS = [
  {
    name: 'Date Updated (Most Recent)',
    field: 'updatedAt',
    direction: 'desc',
    icon: faArrowDownWideShort,
  },
  {
    name: 'A - Z',
    field: 'name',
    direction: 'asc',
    icon: faArrowDownAZ,
  },
  {
    name: 'Z - A',
    field: 'name',
    direction: 'desc',
    icon: faArrowDownZA,
  },
];

const SEARCH_DEBOUNCE_TIME_IN_MS = 500;

/**
 * The SheetModal component.
 */
export class SheetModal extends React.Component {
  /**
   * Determines which Sheet folder is open
   */
  @observable selectedSheetFolder = TAB_MY_SAVED_SHEETS;

    /**
     * Whether or not the sort dropdown is open.
     *
     * @type {boolean}
     */
    @observable isSortDropdownOpen = false;

  /**
   * List of selected time increments
   */
  @observable selectedSheet = null

  @observable sheetFilters = {
    [TAB_MY_SAVED_SHEETS]: {
      search: '',
      searchPlaceholder: 'Search my saved sheets',
      sortOption: SHEET_SORT_OPTIONS[0],
    },
    [TAB_SHEET_TEMPLATES]: {
      search: '',
      searchPlaceholder: 'Search sheet templates',
      sortOption: SHEET_SORT_OPTIONS[0],
    },
  };

  /**
   * Calls the content search request using Lodash's debounce.
   * Waits SEARCH_DEBOUNCE_TIME_IN_MS milliseconds since the last request to fire.
   *
   * @type {function}
   */
  debouncedOnSearch = lodash.debounce(action('debouncedOnSearch', (searchValue, selectedSheetFolder) => {
    this.callSearch(searchValue, selectedSheetFolder);
  }), SEARCH_DEBOUNCE_TIME_IN_MS);

  /**
   * Triggered when the component is mounted to the page.
   */
  componentDidMount() {
    const {apiCompanyScheduleSheetsGetMineStore, apiCompanyScheduleSheetsGetPublicStore} = this.props;
    apiCompanyScheduleSheetsGetMineStore.refresh({
      page: 1,
      search: this.sheetFilters[TAB_MY_SAVED_SHEETS].search,
      sort: this.sheetFilters[TAB_MY_SAVED_SHEETS].sortOption.field,
      direction: this.sheetFilters[TAB_MY_SAVED_SHEETS].sortOption.direction,
    }, true);
    apiCompanyScheduleSheetsGetPublicStore.refresh({
      page: 1,
      search: this.sheetFilters[TAB_SHEET_TEMPLATES].search,
      sort: this.sheetFilters[TAB_SHEET_TEMPLATES].sortOption.field,
      direction: this.sheetFilters[TAB_SHEET_TEMPLATES].sortOption.direction,
    }, true);
  }

  /**
   * Sets which sheet folder is open.
   * @param {'mySavedSheets' | 'sheetTemplates'} sheetFolderKey
   */
  @action setSelectedSheetFolder = (sheetFolderKey) => {
    this.selectedSheetFolder = sheetFolderKey;
  }

  /**
   * Sets which sheet is selected.
   * @param {{}} sheet
   */
  @action setSelectedSheet = (sheet) => {
    const sheetAlreadySelected = (this.selectedSheet && this.selectedSheet.id) === sheet.id;
    if (sheetAlreadySelected) {
      this.selectedSheet = null;
      return;
    }

    this.selectedSheet = sheet;
  }

  /**
   * Opens the sort dropdown
   */
  @action toggleSortDropdown = () => {
    this.isSortDropdownOpen = !this.isSortDropdownOpen;
  };

  submitSheetLayers = () => {
    const {resolution, onComplete} = this.props;
    const sheetHeaderEntities = generateSheetHeaderEntities(this.selectedSheet, resolution);

    onComplete({
      entities: sheetHeaderEntities
    });
  }

  /**
   * Renders the signs.
   * @param {Array<{}>} sheets
   *
   * @returns {Array<{}>}
   */
  parseSheetsToCards = (sheets) => {
    const {userHasAccessToEssential} = this.props;

    return sheets.map((sheet) => {
      const textAndImageHeaders = sheet.headers.filter((sheetHeader) => ['string', 'image'].includes(sheetHeader.type));
      const isSelected = this.selectedSheet && this.selectedSheet.id === sheet.id;
      const userNeedsUpgrade = (sheet.isPaid || (sheet.sourceSheet && sheet.sourceSheet.isPaid)) && !userHasAccessToEssential;

      return (
        <div key={sheet.id} className="col-sm-12 col-lg-6 col-xl-4 p-2">
          <div
            className={classNames('d-flex flex-column sheet-item', {'is-selected': isSelected, 'upgrade-sheet-item': userNeedsUpgrade})}
            onClick={() => this.setSelectedSheet(sheet)}
          >
            <div className="p-2">
              <div className="sheet-item-thumbnail overflow-hidden">
                {(sheet.thumbnailUrl
                  ? (
                    <img
                      src={sheet.thumbnailUrl}
                      alt={`${sheet.name} thumbnail`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )
                  : (
                    <table className="table mt-4">
                      <tbody>
                        {textAndImageHeaders.map((sheetHeader) => (
                          <tr key={sheetHeader.header}>
                            <td className="py-1">{sheetHeader.header}</td>
                          </tr>)
                        )}
                      </tbody>
                    </table>
                  )
                )}
              </div>
              {(sheet.isPaid || (sheet.sourceSheet && sheet.sourceSheet.isPaid))
              && (<Badge className="ml-2 text-light sheet-item-badge" color="info">
                <FontAwesomeIcon icon={faStar} />
                  Included in Essential
              </Badge>)}
            </div>
            <div className="sheet-item-info p-3">
              <div className="sheet-item-name">
                {sheet.name}
              </div>
            </div>
          </div>
          {userNeedsUpgrade && <UpgradeButton />}
        </div>
      );
    });
  }

  /**
   * Event handler when the search form changes
   *
   * @param {{}} changeEvent
   */
  @action searchFormChange = (changeEvent) => {
    const searchText = changeEvent.target.value;
    this.sheetFilters[this.selectedSheetFolder].search = searchText;

    this.debouncedOnSearch(searchText, this.selectedSheetFolder);
  }

  /**
   * Event handler when user changes sort option
   *
   * @param {{}} sortOption
   */
  @action onClickSort = (sortOption) => {
    const {apiCompanyScheduleSheetsGetMineStore, apiCompanyScheduleSheetsGetPublicStore} = this.props;
    const search = this.sheetFilters[this.selectedSheetFolder].search;

    this.sheetFilters[this.selectedSheetFolder].sortOption = sortOption;

    switch (this.selectedSheetFolder) {
      case TAB_MY_SAVED_SHEETS:
        apiCompanyScheduleSheetsGetMineStore.makeRequest({
          page: 1,
          search,
          sort: sortOption.field,
          direction: sortOption.direction,
        });
        break;
      case TAB_SHEET_TEMPLATES:
        apiCompanyScheduleSheetsGetPublicStore.makeRequest({
          page: 1,
          search,
          sort: sortOption.field,
          direction: sortOption.direction,
        });
        break;
      default:

        // do nothing
    }

    this.toggleSortDropdown();
  }

  /**
   * Gets new content based on the search term.
   *
   * @param {string} searchValue
   * @param {string} selectedSheetFolder
   */
  @action callSearch = (searchValue, selectedSheetFolder) => {
    const {apiCompanyScheduleSheetsGetMineStore, apiCompanyScheduleSheetsGetPublicStore} = this.props;

    const sortOption = this.sheetFilters[selectedSheetFolder].sortOption;

    switch (selectedSheetFolder) {
      case TAB_MY_SAVED_SHEETS:
        apiCompanyScheduleSheetsGetMineStore.makeRequest({
          page: 1,
          search: searchValue,
          sort: sortOption.field,
          direction: sortOption.direction,
        });
        break;
      case TAB_SHEET_TEMPLATES:
        apiCompanyScheduleSheetsGetPublicStore.makeRequest({
          page: 1,
          search: searchValue,
          sort: sortOption.field,
          direction: sortOption.direction,
        });
        break;
      default:

        // do nothing
    }
  };

  /**
   * Event handler when the search form is submitted
   *
   * @param {{}} submitEvent
   */
  searchFormSubmit = (submitEvent) => {
    submitEvent.preventDefault();
  };

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {isOpen, onCancelModal, apiCompanyScheduleSheetsGetMineStore, apiCompanyScheduleSheetsGetPublicStore} = this.props;

    const sheets = {
      [TAB_MY_SAVED_SHEETS]: {
        data: apiCompanyScheduleSheetsGetMineStore.getFulfilled() || [],
        meta: apiCompanyScheduleSheetsGetMineStore.getMeta(),
        showLoadMore: apiCompanyScheduleSheetsGetMineStore.hasNextPage(),
        loadMore: () => apiCompanyScheduleSheetsGetMineStore.getNextPage({
          search: this.sheetFilters[this.selectedSheetFolder].search,
          sort: this.sheetFilters[this.selectedSheetFolder].sortOption.field,
          direction: this.sheetFilters[this.selectedSheetFolder].sortOption.direction,
        })
      },
      [TAB_SHEET_TEMPLATES]: {
        data: apiCompanyScheduleSheetsGetPublicStore.getFulfilled() || [],
        meta: apiCompanyScheduleSheetsGetPublicStore.getMeta(),
        showLoadMore: apiCompanyScheduleSheetsGetPublicStore.hasNextPage(),
        loadMore: () => apiCompanyScheduleSheetsGetPublicStore.getNextPage({
          search: this.sheetFilters[this.selectedSheetFolder].search,
          sort: this.sheetFilters[this.selectedSheetFolder].sortOption.field,
          direction: this.sheetFilters[this.selectedSheetFolder].sortOption.direction,
        })
      }
    };

    const sheetFolderOptions = [{key: TAB_MY_SAVED_SHEETS, label: 'My Saved Sheets'}, {key: TAB_SHEET_TEMPLATES, label: 'Sheet Templates'}];

    return (
      <Modal
        isOpen={isOpen}
        toggle={onCancelModal}
        backdrop={true}
        className="sheet-modal modal-dialog-scrollable"
        centered
      >
        <ModalHeader toggle={onCancelModal}>Select Sheet</ModalHeader>
        <ModalBody>
          <div className="sheet-main">
            <div className="left-side">
              {sheetFolderOptions.map((option) => (
                <div
                  key={option.key}
                  className={classNames('sheet-type-list-item d-flex align-items-center', {active: this.selectedSheetFolder === option.key})}
                  onClick={() => this.setSelectedSheetFolder(option.key)}
                >
                  <div>
                    {option.label}
                  </div>
                  <div className="sheet-type-list-item-count ml-auto">
                    {sheets[option.key].meta && sheets[option.key].meta.total}
                  </div>
                </div>
              ))}
            </div>
            <div className="right-side container-fluid">
              <div className="d-flex">
                <div className="search-form-wrapper">
                  <SearchForm
                    placeholder={this.sheetFilters[this.selectedSheetFolder].searchPlaceholder}
                    onChange={this.searchFormChange}
                    onSubmit={this.searchFormSubmit}
                    searchText={this.sheetFilters[this.selectedSheetFolder].search}
                    disabled={false}
                  />
                </div>

                <Dropdown isOpen={this.isSortDropdownOpen} toggle={this.toggleSortDropdown}>
                  <DropdownToggle
                    className="rounded"
                    color="light"
                  >
                    <FontAwesomeIcon icon={this.sheetFilters[this.selectedSheetFolder].sortOption.icon} />
                  </DropdownToggle>
                  <DropdownMenu right>
                    {SHEET_SORT_OPTIONS.map((option) => {
                      return (
                        <DropdownItem
                          key={option.name}
                          onClick={() => this.onClickSort(option)}
                          toggle={false}
                        >
                          <FontAwesomeIcon
                            className="mr-2"
                            icon={option.icon}
                          />
                          {option.name}
                        </DropdownItem>
                      );
                    })}
                  </DropdownMenu>
                </Dropdown>
              </div>
              <div className="row">
                {this.parseSheetsToCards(sheets[this.selectedSheetFolder].data)}
                {(sheets[this.selectedSheetFolder].data.length === 0) && (
                  <div className="col">
                    <div className="py-3 px-2 text-center">
                      <span className="text-muted">No results found. Please try different parameters.</span>
                    </div>
                  </div>
                )}
              </div>
              {sheets[this.selectedSheetFolder].showLoadMore && (<div
                className="row d-flex justify-content-center clickable text-secondary"
                onClick={() => sheets[this.selectedSheetFolder].loadMore()}
              >
                Load More...
              </div>)}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={this.submitSheetLayers}
            disabled={!this.selectedSheet}
          >Start Designing</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

SheetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  resolution: MobxPropTypes.observableObject.isRequired,
  userHasAccessToEssential: PropTypes.bool.isRequired,

  apiCompanyScheduleSheetsGetMineStore: MobxPropTypes.observableObject,
  apiCompanyScheduleSheetsGetPublicStore: MobxPropTypes.observableObject,
};

SheetModal.wrappedComponent = {};
SheetModal.wrappedComponent.propTypes = {
  apiCompanyScheduleSheetsGetMineStore: MobxPropTypes.observableObject.isRequired,
  apiCompanyScheduleSheetsGetPublicStore: MobxPropTypes.observableObject.isRequired,
  resolution: MobxPropTypes.observableObject.isRequired,
};

export default inject(SheetModal)(
  observer(SheetModal)
);
